import { COMPONENT_TRACKING_DEFAULTS } from '../components/Trackable/componentTrackingDefaults';
import type { TrackingMeta } from '../components/Trackable/Trackable';

export interface HostTrackingDocument {
  readonly content?: unknown;
  readonly root?: unknown;
  readonly zones?: unknown;
  [key: string]: unknown;
}

/**
 * Opaque input for a host that wants to overlay element `fields`.
 * CE passes the component type, the node props, the authored field
 * map, and the value returned by `createDocumentContext`. It does
 * not read those values.
 */
export interface HostElementFieldResolution {
  readonly type: string;
  readonly props: Readonly<Record<string, unknown>>;
  readonly authoredFields: Readonly<Record<string, unknown>>;
  readonly documentContext: unknown;
}

export interface ApplyHostTrackingDefaultsOptions {
  /**
   * Optional name for a node whose type is in the host map. Stored
   * on the tracking slice when the node did not author `name`.
   * Unmatched nodes are left unchanged, including an authored `name`.
   */
  readonly resolveName?: (
    type: string,
    props: Readonly<Record<string, unknown>>,
  ) => string | undefined;
  readonly resolveFieldType?: (
    type: string,
    props: Readonly<Record<string, unknown>>,
  ) => string | undefined;
  /**
   * Called once, before the walk. The return value is passed back to
   * `resolveElementFields` and is not inspected.
   */
  readonly createDocumentContext?: (document: HostTrackingDocument) => unknown;
  /**
   * Host overlay for element `fields` on a matched node. Authored
   * `fields` keys win, including `name` and `text`.
   */
  readonly resolveElementFields?: (
    input: HostElementFieldResolution,
  ) => Readonly<Record<string, unknown>> | undefined;
}

const CHOICE_CONTROLS = new Set(['Select', 'MultiSelect', 'ToggleGroup']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function owns(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function pickScalar(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function withoutKey(tracking: TrackingMeta, key: string): TrackingMeta {
  const next: Record<string, unknown> = {};
  for (const [entryKey, entry] of Object.entries(tracking)) {
    if (entryKey === key) continue;
    next[entryKey] = entry;
  }
  return next;
}

function omitAbsentDimensions(tracking: TrackingMeta): TrackingMeta {
  if (tracking.customDimensions !== undefined) return tracking;
  if (!owns(tracking, 'customDimensions')) return tracking;
  return withoutKey(tracking, 'customDimensions');
}

/**
 * One overlay rule for a host default and for an authored node.
 * Keys on the overlay replace the base. `customDimensions` is
 * replaced only when the overlay owns that key, including an empty
 * object. When neither side owns the key, the result omits it.
 */
export function mergeComponentTrackingDefault(
  base: TrackingMeta | undefined,
  overlay: TrackingMeta | undefined,
): TrackingMeta | undefined {
  if (!overlay) return base ? omitAbsentDimensions(base) : undefined;
  if (!base) return omitAbsentDimensions(overlay);

  const overlayOwnsDimensions = owns(overlay, 'customDimensions');
  const { customDimensions: overlayDimensions, ...overlayRest } = overlay;
  const { customDimensions: baseDimensions, ...baseRest } = base;
  const merged: TrackingMeta = { ...baseRest, ...overlayRest };
  if (overlayOwnsDimensions) {
    if (overlayDimensions === undefined) return omitAbsentDimensions(merged);
    return omitAbsentDimensions({
      ...merged,
      customDimensions: overlayDimensions,
    });
  }
  if (owns(base, 'customDimensions') && baseDimensions !== undefined) {
    return omitAbsentDimensions({
      ...merged,
      customDimensions: baseDimensions,
    });
  }
  return omitAbsentDimensions(merged);
}

/**
 * Layers a host map onto the built-in registry. A host entry fills
 * only the keys it sets, so `{ Input: { kind: 'field' } }` keeps the
 * registry `fieldType`. An owned `customDimensions` value replaces
 * that map. Types the registry does not know are added as given.
 *
 * `DslConfig` reads the provider `componentTrackingDefaults` through
 * this function. `applyHostTrackingDefaults` is the separate adapter
 * for a raw document that is not rendered by `DslConfig`.
 */
export function overlayComponentTrackingDefaults(
  registry: Readonly<Record<string, TrackingMeta>>,
  hostDefaults: Readonly<Record<string, TrackingMeta>> | undefined,
): Readonly<Record<string, TrackingMeta>> {
  if (!hostDefaults) return registry;
  const merged: Record<string, TrackingMeta> = { ...registry };
  for (const [type, hostMeta] of Object.entries(hostDefaults)) {
    const combined = mergeComponentTrackingDefault(registry[type], hostMeta);
    if (combined) merged[type] = combined;
  }
  return merged;
}

function derivedChoiceFieldType(
  type: string,
  props: Readonly<Record<string, unknown>>,
): 'select' | 'multi-select' | undefined {
  if (!CHOICE_CONTROLS.has(type)) return undefined;
  if (type === 'MultiSelect') return 'multi-select';
  if (type === 'ToggleGroup' && props.type === 'multiple') {
    return 'multi-select';
  }
  return 'select';
}

function stampMatchedNode(
  type: string,
  props: Record<string, unknown>,
  hostEntry: TrackingMeta,
  options: ApplyHostTrackingDefaultsOptions,
  documentContext: unknown,
): Record<string, unknown> {
  const effective =
    mergeComponentTrackingDefault(
      COMPONENT_TRACKING_DEFAULTS[type],
      hostEntry,
    ) ?? hostEntry;
  const authored = isPlainObject(props['_meta.tracking'])
    ? (props['_meta.tracking'] as TrackingMeta)
    : undefined;
  const merged =
    mergeComponentTrackingDefault(effective, authored) ?? effective;
  const resolvedName =
    options.resolveName && !(authored && owns(authored, 'name'))
      ? pickScalar(options.resolveName(type, props))
      : undefined;
  const withResolvedName = resolvedName
    ? { ...merged, name: resolvedName }
    : merged;

  if (withResolvedName.kind === 'field') {
    return {
      ...props,
      '_meta.tracking': stampField(
        type,
        props,
        effective,
        authored,
        withResolvedName,
        options,
      ),
    };
  }

  if (withResolvedName.kind === 'element') {
    return {
      ...props,
      '_meta.tracking': stampElement(
        type,
        props,
        effective,
        authored,
        withResolvedName,
        options,
        documentContext,
      ),
    };
  }

  return { ...props, '_meta.tracking': withResolvedName };
}

function stampField(
  type: string,
  props: Record<string, unknown>,
  effective: TrackingMeta,
  authored: TrackingMeta | undefined,
  merged: TrackingMeta,
  options: ApplyHostTrackingDefaultsOptions,
): TrackingMeta {
  const authoredFieldType = pickScalar(authored?.fieldType);
  const authoredChoiceType =
    authoredFieldType === 'select' || authoredFieldType === 'multi-select'
      ? authoredFieldType
      : undefined;
  const fieldType =
    authoredChoiceType ??
    options.resolveFieldType?.(type, props) ??
    authoredFieldType ??
    pickScalar(effective.fieldType) ??
    derivedChoiceFieldType(type, props);
  const fieldName =
    pickScalar(authored?.fieldName) ??
    pickScalar(effective.fieldName) ??
    pickScalar(props.formKey);
  return omitAbsentDimensions(
    withoutKey(
      {
        ...merged,
        kind: 'field',
        ...(fieldType ? { fieldType } : {}),
        ...(fieldName ? { fieldName } : {}),
      },
      'leadFieldAssociated',
    ),
  );
}

function stampElement(
  type: string,
  props: Record<string, unknown>,
  effective: TrackingMeta,
  authored: TrackingMeta | undefined,
  merged: TrackingMeta,
  options: ApplyHostTrackingDefaultsOptions,
  documentContext: unknown,
): TrackingMeta {
  const defaultFields = isPlainObject(effective.fields) ? effective.fields : {};
  const authoredFields = isPlainObject(authored?.fields) ? authored.fields : {};
  const hostFields = options.resolveElementFields?.({
    type,
    props,
    authoredFields,
    documentContext,
  });
  const fields: Record<string, unknown> = {
    ...defaultFields,
    ...(isPlainObject(hostFields) ? hostFields : {}),
    ...authoredFields,
  };
  const hasFields =
    Object.keys(fields).length > 0 ||
    (authored !== undefined && owns(authored, 'fields')) ||
    owns(effective, 'fields') ||
    hostFields !== undefined;
  const tracking: TrackingMeta = {
    ...merged,
    ...(hasFields ? { fields } : {}),
  };
  return omitAbsentDimensions(tracking);
}

function enrichValue(
  value: unknown,
  defaults: Readonly<Record<string, TrackingMeta>>,
  options: ApplyHostTrackingDefaultsOptions,
  documentContext: unknown,
): unknown {
  if (Array.isArray(value)) {
    return value.map((child) =>
      enrichValue(child, defaults, options, documentContext),
    );
  }
  if (!isPlainObject(value)) return value;

  if (typeof value.type === 'string' && isPlainObject(value.props)) {
    const props = Object.fromEntries(
      Object.entries(value.props).map(([key, child]) => [
        key,
        enrichValue(child, defaults, options, documentContext),
      ]),
    );
    const hostEntry = defaults[value.type];
    if (!hostEntry) {
      return { ...value, props };
    }
    return {
      ...value,
      props: stampMatchedNode(
        value.type,
        props,
        hostEntry,
        options,
        documentContext,
      ),
    };
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      enrichValue(child, defaults, options, documentContext),
    ]),
  );
}

/**
 * Copies host tracking defaults onto a render document. The saved
 * document is not mutated. Only component types named by the host
 * map are stamped. A sparse host entry inherits the built-in
 * registry values it does not replace, and an authored
 * `customDimensions` map replaces the default map.
 *
 * Provider `componentTrackingDefaults` changes what `DslConfig`
 * renders. This function is the adapter for a raw document. It is
 * exported from the client `ce-runtime` entry and must be called
 * from client code. It is not part of the server-safe `utils` entry.
 */
export function applyHostTrackingDefaults<T extends HostTrackingDocument>(
  data: T,
  defaults: Readonly<Record<string, TrackingMeta>>,
  options: ApplyHostTrackingDefaultsOptions = {},
): T {
  const documentContext = options.createDocumentContext?.(data);
  return enrichValue(data, defaults, options, documentContext) as T;
}
